// Pre-draw checks for status-trigger carriers reached by this use.
// apply_status_effect_inc.php fires triggers_when_caster_gains_status_effect on the GAINER and
// triggers_when_target_gains_status_effect on the APPLIER; status_removal_api.php fires triggers_when_caster_loses_status_effect.
// janus_trigger() walks janus_trigger_carriers() through janus_trigger_carrier_entries() (layer_entries ∪ the raw root slot, deduped by
// id; spells_known hydrated), plus each held status's status_arr, and janus_trigger_entry_predicate_match() fires only when the
// carrier's list NAMES the inciting status catalog id (layer_list_entry_uuid normalisation; an empty list never fires).
import { type JanusCtx, type Row, HandBack, activeStatuses, ecId, handBack, itemField, record, upper } from '../context.js';
import { triggerFamilyPreDraw } from '../trigger-executor.js';
import { heldStatusDefinition } from '../../reactions/trigger-dispatch.js';

/** janus_trigger_carriers.php */
const CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest',
  'spells_known', 'opportunity_attack_ability'];
const PC_DYING_STATUS_IDS = ['99C862B9-D6AE-4E18-7803-A6F9B42A88E1', '8AE129AF-52D8-CCE8-706A-85767DB52740', 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D'];
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';

/** layer_list_entry_uuid(): item_layer_effect, then items.item_id, then item_id, then a bare string — each cut at '|'. */
export function layerListEntryUuid(entry: unknown): string {
  const cut = (value: unknown): string => typeof value === 'string' && value !== '' ? upper(value.split('|')[0]) : '';
  const row = record(entry);
  if (row) return cut(row.item_layer_effect) || cut(record(row.items)?.item_id) || cut(row.item_id);
  return cut(entry);
}
function names(ctx: JanusCtx, carrier: Row, key: string, statusIds: ReadonlySet<string>): boolean {
  return ctx.read.entries(carrier, key).some((entry) => statusIds.has(layerListEntryUuid(entry)));
}
/** A carrier entry's full definition: a nested item_arr only when it carries layers (a bare {items:{item_id}} shell does not). */
export function carrierDefinition(ctx: JanusCtx, entry: unknown, reason: string): Row | null {
  const row = record(entry);
  if (row?.passive_conditions_active === false) return null;
  const nested = record(row?.item_arr);
  if (nested && record(nested.item_layer_types)) return nested;
  const id = layerListEntryUuid(nested ?? entry) || upper(row?.ability_id);
  const definition = id ? ctx.read.definition(id) : null;
  if (!definition) handBack(`${reason}:carrier-definition-not-held:${id}`);
  return definition;
}

export function carrierHasTrigger(ctx: JanusCtx, owner: Row, key: string, reason: string, statusItemIds: string | Iterable<string>): boolean {
  if (ctx.statusTriggersMuted) return false;
  const statusIds = new Set([...(typeof statusItemIds === 'string' ? [statusItemIds] : statusItemIds)].map(upper));
  if (!statusIds.size) return false;
  if (names(ctx, owner, key, statusIds)) return true;
  const abilities = record(record(record(owner.computed)?.creature)?.abilities) ?? {};
  const creature = record(record(owner.computed)?.creature) ?? {};
  for (const carrier of CARRIERS) {
    const seen = new Set<string>();
    const pool = [...ctx.read.entries(owner, carrier), ...(Array.isArray(owner[carrier]) ? owner[carrier] as unknown[] : []),
      ...(Array.isArray(abilities[carrier]) ? abilities[carrier] as unknown[] : []),
      ...(carrier === 'spells_known' && Array.isArray(creature.spells_known) ? creature.spells_known as unknown[] : [])];
    for (const entry of pool) {
      const id = layerListEntryUuid(record(entry)?.item_arr ?? entry) || upper(record(entry)?.ability_id);
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      const definition = carrierDefinition(ctx, entry, reason);
      if (definition && names(ctx, definition, key, statusIds)) return true;
    }
  }
  for (const status of activeStatuses(ctx, owner)) {
    const definition = heldStatusDefinition({ definition: ctx.read.definition, itemField }, status, owner);
    if (!definition) handBack(`${reason}:status-definition-not-held`);
    if (names(ctx, definition, key, statusIds)) return true;
  }
  return false;
}

/**
 * PRE-DRAW: every status id this use could apply — its status_effect_applied_* layers, the ability itself as an item-as-status,
 * Concentrating, and (a damaging use) apply_player_dying's cascade, which only lands on MID rolls the extreme-dice dry run never
 * samples. Only possible recipients and the caster can gain these statuses. The actual
 * applicator checks nested/redirected effects in their own frames before applying them.
 */
export function statusGainTriggerPossible(ctx: JanusCtx, ability: Row, caster: Row, targetIds: Iterable<string>): string | null {
  const ids = new Set<string>([upper(itemField(ability, 'item_id')), CONCENTRATING]);
  const layers = Object.keys(record(ability.item_layer_types) ?? {}).filter((key) => key.startsWith('status_effect_applied'));
  for (const key of layers) for (const entry of ctx.read.entries(ability, key)) { const id = layerListEntryUuid(entry); if (id) ids.add(id); }
  if (ctx.read.entries(ability, 'damage_type').length) for (const id of PC_DYING_STATUS_IDS) ids.add(id);
  const blocked = (owner: Row, trigger: string): string | null => {
    for (const id of ids) {
      if (!carrierHasTrigger(ctx, owner, trigger, 'janus:gain-trigger', id)) continue;
      try { triggerFamilyPreDraw(ctx, owner, { trigger, statusItemId: id }); }
      catch (error) { if (error instanceof HandBack) return error.reason; throw error; }
    }
    return null;
  };
  for (const id of new Set([ecId(caster), ...targetIds])) {
    const member = ctx.members.get(id);
    if (!member) continue;
    const reason = blocked(member, 'triggers_when_caster_gains_status_effect');
    if (reason) return reason;
  }
  const applier = ctx.members.get(ecId(caster));
  if (!applier) return carrierHasTrigger(ctx, caster, 'triggers_when_target_gains_status_effect', 'janus:gain-trigger', ids) ? 'applier-not-member' : null;
  return blocked(applier, 'triggers_when_target_gains_status_effect');
}

/**
 * PRE-DRAW: does any janus_trigger() carrier of `owner` (the nine carrier buckets, the raw root slot, spells_known, and each active
 * status's definition) AUTHOR one of `keys` at all? For trigger families whose predicate the browser does not dispatch (the damage
 * fan-out's triggers_when_{caster,target}_takes_{any_damage,damage_type}) presence alone hands back. `skip` leaves out carriers a
 * caller models itself (the target's reaction_ability — damageTakenReactions).
 */
export function carrierAuthorsAny(ctx: JanusCtx, owner: Row, keys: readonly string[], reason: string, skip: readonly string[] = []): boolean {
  const authors = (row: Row): boolean => keys.some((key) => ctx.read.has(row, key));
  if (authors(owner)) return true;
  const abilities = record(record(record(owner.computed)?.creature)?.abilities) ?? {};
  const creature = record(record(owner.computed)?.creature) ?? {};
  for (const carrier of CARRIERS) {
    if (skip.includes(carrier)) continue;
    const seen = new Set<string>();
    const pool = [...ctx.read.entries(owner, carrier), ...(Array.isArray(owner[carrier]) ? owner[carrier] as unknown[] : []),
      ...(Array.isArray(abilities[carrier]) ? abilities[carrier] as unknown[] : []),
      ...(carrier === 'spells_known' && Array.isArray(creature.spells_known) ? creature.spells_known as unknown[] : [])];
    for (const entry of pool) {
      const id = layerListEntryUuid(record(entry)?.item_arr ?? entry) || upper(record(entry)?.ability_id);
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      const definition = carrierDefinition(ctx, entry, reason);
      if (definition && authors(definition)) return true;
    }
  }
  for (const status of activeStatuses(ctx, owner)) {
    const definition = heldStatusDefinition({ definition: ctx.read.definition, itemField }, status, owner);
    if (!definition) handBack(`${reason}:status-definition-not-held`);
    if (authors(definition)) return true;
  }
  return false;
}
